//* Libraries imports
import { useState, useCallback } from "react";

//* Config imports
import { API_URL } from "@/config/api";

//* Types imports
import type { ClientOutputDTO } from "@/features/Client/types";
import type { MappingRule } from "@/features/template/types";

export type FilledFormData = {
  field: {
    _id: string;
    options?: { key: string; value: string }[];
  };
  data: string | string[] | number | boolean | null;
};

export type FilledForm = {
  _id: string;
  phaseFormId: string;
  processId: string;
  data: FilledFormData[];
};

export type Process = {
  _id: string;
  filledForms: FilledForm[];
};

export type HistoryItem = {
  _id: string;
  createdAt: string;
  type: "automatic" | "upload";
  clientId: string;
  url?: string;
  documentUrl?: string;
};

export const useProcessMapping = () => {
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [notFoundFormRef, setNotFoundFormRef] = useState<
    Record<string, boolean>
  >({});
  const [completedClientRef, setCompletedClientRef] = useState<
    Record<string, any>
  >({});
  const [phaseFormFilledRef, setPhaseFormFilledRef] = useState<
    Record<string, FilledForm>
  >({});

  const loadProcessClient = useCallback(
    async (
      clientId: string,
      clientList: ClientOutputDTO[],
      mappings?: MappingRule[]
    ) => {
      setIsLoadingData(true);
      try {
        const client = clientList.find((c: any) => c._id === clientId);
        if (!client) return;

        const response = await fetch(
          `${API_URL}/processes?clientId=${client._id}&whatsapp=${client.whatsapp}`,
          {
            headers: {
              Authorization: localStorage.getItem("token") || "",
              Accept: "application/json",
            },
          }
        );
        const processes: Process[] = await response.json();

        const filled: Record<string, FilledForm> = {};
        processes.forEach((p) =>
          p.filledForms.forEach((f) => (filled[f.phaseFormId] = f))
        );

        const notFound: Record<string, boolean> = {};
        const notComp: Record<string, any> = {};

        mappings?.forEach((m) => {
          if (
            m.type === "form" &&
            m.config.phaseFormId &&
            !filled[m.config.phaseFormId]
          ) {
            notFound[m.config.phaseFormId] = true;
          } else if (m.type === "client" && m.config.field) {
            const field = m.config.field;
            const val = (client as any)[
              field === "full_address" ? "places" : field
            ];
            if (!val || (Array.isArray(val) && val.length === 0))
              notComp[field] = "";
          }
        });

        setNotFoundFormRef(notFound);
        setCompletedClientRef(notComp);
        setPhaseFormFilledRef(filled);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoadingData(false);
      }
    },
    []
  );

  return {
    loadProcessClient,
    isLoadingData,
    setIsLoadingData,
    notFoundFormRef,
    completedClientRef,
    phaseFormFilledRef,
  };
};
